import React, { ReactNode, useCallback, useRef } from 'react'
import clsx from 'clsx'

import { BaseButton } from '@loadsmart/loadsmart-ui'
import Icon from '../Icon'
import useID from '../../hooks/useID'

import './CopyToClipboard.css'

export interface CopyToClipboardProps {
  id?: string | undefined
  className?: string
  buttonLocation?: 'before' | 'after'
  value: number | string
  disabled?: boolean
  hidden?: boolean
  leading?: ReactNode
  trailing?: ReactNode
}

function CopyToClipboard({
  className,
  id: idProp,
  buttonLocation = 'after',
  value,
  disabled,
  hidden,
  leading,
  trailing,
}: CopyToClipboardProps) {
  const id = useID({ id: idProp })
  const inputRef = useRef<HTMLInputElement>(null)

  const handleCopy = useCallback(() => {
    // based on https://www.w3schools.com/howto/howto_js_copy_clipboard.asp

    const copyText = inputRef.current

    if (!copyText) {
      return
    }

    copyText.select()
    copyText.setSelectionRange(0, 99999) /* For mobile devices */

    /* Copy the text inside the text field */
    document.execCommand('copy')
  }, [])

  const ButtonAction = () => (
    <BaseButton
      className="ml-4 mr-4 rg-copy-to-clipboard-button"
      onClick={handleCopy}
      disabled={disabled}
      data-testid="copy-button"
    >
      <Icon name="copy" aria-label="Copy to Clipboard" size={16} />
    </BaseButton>
  )

  return (
    <div className={clsx('flex flex-row items-center text-sm', className)}>
      {buttonLocation === 'before' && <ButtonAction />}

      <input
        ref={inputRef}
        type="text"
        id={id}
        className="outline-none py-1.5 cursor-default visually-hidden"
        defaultValue={value}
        data-testid="hidden-input"
        tabIndex={-1}
      />

      {leading && <span className="inline-block mr-2 truncate pointer-events-none">{leading}</span>}

      {!hidden && (
        <span title={String(value)} className="truncate pointer-events-none">
          {value}
        </span>
      )}

      {trailing && (
        <span className="inline-block ml-2 truncate pointer-events-none">{trailing}</span>
      )}
      {buttonLocation === 'after' && <ButtonAction />}
    </div>
  )
}

export default CopyToClipboard
